import { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import type { SequenceWithRelations, YearWithProjects } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";

interface EditSequenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sequence: SequenceWithRelations;
  projectId: string;
}

export function EditSequenceDialog({
  open,
  onOpenChange,
  sequence,
  projectId
}: EditSequenceDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [sequenceName, setSequenceName] = useState(sequence.name || "");
  const [leadArtist, setLeadArtist] = useState<string>(sequence.leadArtist || "none");

  // Fetch years data to find existing sequences
  const { data: years } = useQuery<YearWithProjects[]>({
    queryKey: ["/api/years"],
  });

  // Fetch artists for artist selection
  const { data: artists = [] } = useQuery({
    queryKey: ['/api/artists'],
  });

  // Reset form when dialog opens or sequence changes
  useEffect(() => {
    if (open) {
      setSequenceName(sequence.name || "");
      setLeadArtist(sequence.leadArtist || "none");
    }
  }, [open, sequence]);

  const updateSequenceMutation = useMutation({
    mutationFn: async (sequenceData: any) => {
      const response = await apiRequest('PATCH', `/api/sequences/${sequence.id}`, sequenceData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      toast({
        title: "Success",
        description: "Sequence updated successfully",
      });
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update sequence",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    updateSequenceMutation.mutate({
      name: sequenceName || null, // Send null if empty string
      leadArtist: leadArtist === "none" ? null : leadArtist // Send null instead of empty string
    });
  };



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] navy-card border-gray-600 overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl text-gray-100">Edit Sequence</DialogTitle>
          <DialogDescription className="text-gray-400">
            Modify sequence name and lead artist assignment
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sequence Details Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-2">Sequence Details</h3>
            
            {/* Read-only Sequence Number Display */}
            <div>
              <Label className="text-gray-300">Sequence Number</Label>
              <div className="px-3 py-2 navy-bg border border-gray-600 rounded-md text-gray-400 bg-gray-800">
                Seq. {sequence.sequenceNumber.toString().padStart(2, '0')} (Cannot be changed)
              </div>
            </div>

            <div>
              <Label htmlFor="sequenceName" className="text-gray-300">Sequence Name (Optional)</Label>
              <Input
                id="sequenceName"
                value={sequenceName}
                onChange={(e) => setSequenceName(e.target.value)}
                placeholder="Enter sequence name..."
                className="navy-bg border-gray-600 text-white placeholder-gray-500"
              />
            </div>
            
            <div>
              <Label htmlFor="leadArtist" className="text-gray-300">Lead Artist</Label>
              <Select value={leadArtist} onValueChange={setLeadArtist}>
                <SelectTrigger className="navy-bg border-gray-600 text-white">
                  <SelectValue placeholder="Select lead artist" />
                </SelectTrigger>
                <SelectContent className="navy-card border-gray-600">
                  <SelectItem value="none" className="text-white">No lead artist</SelectItem>
                  {(artists as any[]).map((artist) => (
                    <SelectItem key={artist.id} value={artist.shortCode} className="text-white">
                      {artist.firstName} {artist.lastName} ({artist.shortCode})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-6 border-t border-gray-600">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={updateSequenceMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
            >
              {updateSequenceMutation.isPending ? "Updating..." : "Update Sequence"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}